// src/components/users/OfficerEquipmentModal.tsx
// ✅ LOGGING: Complete migration to centralized logging system

import React, { useState, useEffect } from 'react';
import { User } from '../../types/user';
import { Assignment } from '../../types/assignment';
import { assignmentAPI } from '../../services/assignmentAPI';
import { EquipmentItemCard } from './EquipmentItemCard';
import ReturnItemFromOfficerModal from './ReturnItemFromOfficerModal';
import { tenantConfigAPI } from '../../services/tenantConfigAPI';
import { logger } from '../../utils/logger';
import { 
  Search, 
  Package, 
  Printer, 
  X, 
  RefreshCw,
  Zap,
  Shield, 
  Radio, 
  Car, 
  Shirt,
  Monitor,
  Heart,
  Wrench,
  Box,
  Building,
  Clock,
  FileText
} from 'lucide-react';

// ✅ LOGGING: Create dedicated logger for Officer Equipment Modal
const officerEquipmentLogger = logger.createServiceLogger('OfficerEquipment');

interface OfficerEquipmentModalProps {
  isOpen: boolean;
  officer: User | null;
  onClose: () => void;
  onEquipmentUpdated?: () => void;
}

// ✅ HELPER: Dynamic category matching
const matchesCategory = (itemName: string, category: any): boolean => {
  const name = itemName.toLowerCase();
  
  // If it's a hierarchical category object
  if (typeof category === 'object' && category.name) {
    const categoryName = category.name.toLowerCase();
    return name.includes(categoryName) || categoryName.includes(name);
  }
  
  // If it's a simple string category
  if (typeof category === 'string') {
    const categoryName = category.toLowerCase();
    return name.includes(categoryName) || categoryName.includes(name);
  }
  
  return false;
};

// ✅ FIXED: Enhanced search function using actual Assignment properties
const matchesSearch = (assignment: Assignment, searchTerm: string): boolean => {
  const search = searchTerm.toLowerCase().trim();
  
  if (!search) return true;
  
  // Search through actual assignment fields from the Assignment type
  const searchFields = [
    assignment.itemName,                  // Item name
    assignment.itemSerialNumber,          // Serial number (optional)
    assignment.assignmentReason,          // Assignment reason
    assignment.assignedToUserName,        // Officer name
    assignment.assignedToUserBadge,       // Badge number
    assignment.assignedByUserName,        // Assigned by name (optional)
    assignment.returnCondition,           // Return condition (optional)
    assignment.returnNotes,               // Return notes (optional) - this exists!
    assignment.status                     // Assignment status
  ].filter(Boolean); // Remove null/undefined values
  
  return searchFields.some(field => 
    field && field.toLowerCase().includes(search)
  );
};

// ✅ HELPER: Get appropriate Lucide icon for category (for headers only)
const getCategoryIcon = (category: any) => {
  if (!category) return Package;
  
  const name = (category.name || category.categoryId || category).toLowerCase();
  
  if (name.includes('firearm') || name.includes('weapon') || name.includes('gun')) {
    return Zap;
  } else if (name.includes('protective') || name.includes('armor') || name.includes('vest') || name.includes('helmet')) {
    return Shield;
  } else if (name.includes('communication') || name.includes('radio') || name.includes('phone')) {
    return Radio;
  } else if (name.includes('uniform') || name.includes('clothing') || name.includes('shirt') || name.includes('pants')) {
    return Shirt;
  } else if (name.includes('vehicle') || name.includes('car') || name.includes('truck')) {
    return Car;
  } else if (name.includes('electronics') || name.includes('computer') || name.includes('tablet')) {
    return Monitor;
  } else if (name.includes('medical') || name.includes('health') || name.includes('first aid')) {
    return Heart;
  } else if (name.includes('tools') || name.includes('equipment')) {
    return Wrench;
  } else if (name.includes('office') || name.includes('admin') || name.includes('supplies')) {
    return Building;
  } else if (name.includes('training') || name.includes('education')) {
    return FileText;
  } else if (name.includes('evidence') || name.includes('storage')) {
    return Box;
  } else if (name.includes('maintenance') || name.includes('repair')) {
    return Clock;
  } else {
    return Package;
  }
};

const OfficerEquipmentModal: React.FC<OfficerEquipmentModalProps> = ({
  isOpen,
  officer,
  onClose,
  onEquipmentUpdated
}) => {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [filteredAssignments, setFilteredAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  
  // ✅ NEW: Dynamic categories
  const [categories, setCategories] = useState<any[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const loadOfficerEquipment = async () => {
    if (!officer) return;

    const timer = logger.startTimer('OfficerEquipment', 'loadOfficerEquipment');
    
    try {
      setLoading(true);
      setError('');
      
      officerEquipmentLogger.info('Loading equipment for officer', {
        officerId: officer.userId,
        officerName: `${officer.firstName} ${officer.lastName}`,
        badgeNumber: officer.badgeNumber
      });
      
      const response = await assignmentAPI.getOfficerEquipmentCompat(officer.userId);
      
      officerEquipmentLogger.info('Officer equipment loaded successfully', {
        officerId: officer.userId,
        equipmentCount: response.length
      });
      
      setAssignments(response);
      setFilteredAssignments(response);
    } catch (err) {
      const errorMessage = 'Failed to load officer equipment. Please try again.';
      officerEquipmentLogger.error('Error loading officer equipment', {
        officerId: officer.userId,
        error: err instanceof Error ? err.message : 'Unknown error'
      }, err instanceof Error ? err : undefined);
      
      setError(errorMessage);
    } finally {
      setLoading(false);
      timer(); // Complete timing
    }
  };

  // ✅ FIXED: Load dynamic categories with better error handling
  const loadCategories = async () => {
    const timer = logger.startTimer('OfficerEquipment', 'loadCategories');
    setCategoriesLoading(true);
    
    try {
      officerEquipmentLogger.debug('Loading inventory categories for filtering');
      
      const response = await tenantConfigAPI.getConfiguration('inventory.categories');
      
      officerEquipmentLogger.debug('Categories API response received', {
        success: response.success,
        hasData: !!response.data?.configuration?.values
      });
      
      if (response.success && response.data?.configuration?.values) {
        let categoryData = response.data.configuration.values;
        
        // Handle different formats - values could be JSON strings or plain strings
        if (categoryData.length > 0) {
          try {
            // Try to parse as JSON first (hierarchical categories)
            const parsedCategories = JSON.parse(categoryData[0]);
            if (Array.isArray(parsedCategories)) {
              setCategories(parsedCategories);
              officerEquipmentLogger.info('Loaded hierarchical categories', {
                categoryCount: parsedCategories.length,
                categories: parsedCategories.map(cat => cat.name || cat.categoryId)
              });
            } else {
              throw new Error('Not an array');
            }
          } catch (parseError) {
            // Fallback to treating as simple string array
            const simpleCategories = categoryData.map(cat => ({ 
              name: cat, 
              categoryId: cat.toLowerCase().replace(/\s+/g, '-') 
            }));
            setCategories(simpleCategories);
            officerEquipmentLogger.info('Loaded simple categories', {
              categoryCount: simpleCategories.length,
              categories: simpleCategories.map(cat => cat.name)
            });
          }
        }
      } else {
        officerEquipmentLogger.warn('No categories found in API response, using fallback defaults');
        // Fallback to basic categories if none configured
        const fallbackCategories = [
          { name: 'Firearms', categoryId: 'firearms' },
          { name: 'Protective Gear', categoryId: 'protective' },
          { name: 'Communication', categoryId: 'communication' },
          { name: 'Uniform Items', categoryId: 'uniform' },
          { name: 'Vehicles', categoryId: 'vehicle' }
        ];
        setCategories(fallbackCategories);
        officerEquipmentLogger.info('Using fallback categories', {
          categoryCount: fallbackCategories.length
        });
      }
    } catch (err) {
      officerEquipmentLogger.error('Error loading categories, using fallback', {
        error: err instanceof Error ? err.message : 'Unknown error'
      }, err instanceof Error ? err : undefined);
      
      // Use fallback categories
      const fallbackCategories = [
        { name: 'Firearms', categoryId: 'firearms' },
        { name: 'Protective Gear', categoryId: 'protective' },
        { name: 'Communication', categoryId: 'communication' },
        { name: 'Uniform Items', categoryId: 'uniform' },
        { name: 'Vehicles', categoryId: 'vehicle' }
      ];
      setCategories(fallbackCategories);
      officerEquipmentLogger.info('Applied fallback categories after error', {
        categoryCount: fallbackCategories.length
      });
    } finally {
      setCategoriesLoading(false);
      timer(); // Complete timing
    }
  };

  useEffect(() => {
    if (isOpen && officer) {
      officerEquipmentLogger.debug('Modal opened, initializing data load', {
        officerId: officer.userId,
        officerName: `${officer.firstName} ${officer.lastName}`
      });
      
      loadOfficerEquipment();
      loadCategories();
      setSearchTerm('');
      setSelectedFilter('all');
    }
  }, [isOpen, officer]);

  // ✅ FIXED: Enhanced filtering with better search and logging
  useEffect(() => {
    const timer = logger.startTimer('OfficerEquipment', 'filterAssignments');
    
    officerEquipmentLogger.debug('Applying filters to assignments', {
      searchTerm,
      selectedFilter,
      totalAssignments: assignments.length
    });
    
    let filtered = assignments;

    // Apply search filter first
    if (searchTerm.trim()) {
      officerEquipmentLogger.debug('Applying search filter', { searchTerm });
      const beforeCount = filtered.length;
      
      filtered = filtered.filter(assignment => {
        const matches = matchesSearch(assignment, searchTerm);
        return matches;
      });
      
      officerEquipmentLogger.debug('Search filter applied', {
        searchTerm,
        beforeCount,
        afterCount: filtered.length,
        filteredOut: beforeCount - filtered.length
      });
    }

    // Apply category filter second
    if (selectedFilter !== 'all' && categories.length > 0) {
      officerEquipmentLogger.debug('Applying category filter', { selectedFilter });
      
      const selectedCategory = categories.find(cat => 
        cat.categoryId === selectedFilter || 
        cat.name?.toLowerCase().replace(/\s+/g, '-') === selectedFilter ||
        cat.name?.toLowerCase().replace(/\s+/g, '') === selectedFilter
      );
      
      if (selectedCategory) {
        const beforeCount = filtered.length;
        
        filtered = filtered.filter(assignment => {
          const matches = matchesCategory(assignment.itemName, selectedCategory);
          return matches;
        });
        
        officerEquipmentLogger.debug('Category filter applied', {
          selectedCategory: selectedCategory.name,
          beforeCount,
          afterCount: filtered.length,
          filteredOut: beforeCount - filtered.length
        });
      }
    }

    officerEquipmentLogger.info('Assignment filtering completed', {
      originalCount: assignments.length,
      finalCount: filtered.length,
      searchTerm: searchTerm || null,
      categoryFilter: selectedFilter !== 'all' ? selectedFilter : null
    });
    
    setFilteredAssignments(filtered);
    timer(); // Complete timing
  }, [searchTerm, selectedFilter, assignments, categories]);

  const handleReturnItem = (assignment: Assignment) => {
    officerEquipmentLogger.info('Initiating equipment return', {
      assignmentId: assignment.assignmentId,
      itemName: assignment.itemName,
      officerId: assignment.assignedToUserId
    });
    
    setSelectedAssignment(assignment);
    setReturnModalOpen(true);
  };

  const handleReturnComplete = async () => {
    officerEquipmentLogger.info('Equipment return completed, refreshing data');
    
    setReturnModalOpen(false);
    setSelectedAssignment(null);
    await loadOfficerEquipment(); // Refresh the equipment list
    onEquipmentUpdated?.(); // Notify parent component
  };

  const handlePrint = () => {
    officerEquipmentLogger.info('Generating equipment report for printing', {
      officerId: officer?.userId,
      equipmentCount: filteredAssignments.length
    });
    
    const printContent = `
      Officer Equipment Report
      
      Officer: ${officer?.firstName} ${officer?.lastName}
      Badge: ${officer?.badgeNumber}
      Department: ${officer?.department}
      Date: ${new Date().toLocaleDateString()}
      
      Assigned Equipment (${filteredAssignments.length} items):
      
      ${filteredAssignments.map(assignment => 
        `• ${assignment.itemName}${assignment.itemSerialNumber ? ` (Serial: ${assignment.itemSerialNumber})` : ''}
         Assigned: ${new Date(assignment.assignmentDate).toLocaleDateString()}`
      ).join('\n\n')}
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Officer Equipment Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              pre { white-space: pre-wrap; }
            </style>
          </head>
          <body>
            <pre>${printContent}</pre>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
      
      officerEquipmentLogger.info('Equipment report sent to printer');
    } else {
      officerEquipmentLogger.error('Failed to open print window');
    }
  };

  if (!isOpen || !officer) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {officer.firstName} {officer.lastName}'s Equipment
              </h2>
              <p className="text-sm text-gray-600">
                Badge {officer.badgeNumber} • {officer.rank} • {assignments.length} items assigned
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="p-2 text-gray-400 hover:text-gray-600 focus:outline-none"
              title="Print Equipment List"
            >
              <Printer className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="p-6 border-b bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* ✅ FIXED: Enhanced Search Input */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by item name, serial number, assignment reason, status..."
                  value={searchTerm}
                  onChange={(e) => {
                    const newTerm = e.target.value;
                    officerEquipmentLogger.debug('Search term changed', {
                      previousTerm: searchTerm,
                      newTerm
                    });
                    setSearchTerm(newTerm);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Category Filter */}
            <div className="sm:w-56 relative">
              <select
                value={selectedFilter}
                onChange={(e) => {
                  const newFilter = e.target.value;
                  officerEquipmentLogger.debug('Category filter changed', {
                    previousFilter: selectedFilter,
                    newFilter
                  });
                  setSelectedFilter(newFilter);
                }}
                disabled={categoriesLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                <option value="all">
                  {categoriesLoading ? 'Loading Categories...' : 'All Categories'}
                </option>
                {categories.map((category) => {
                  const categoryId = category.categoryId || category.name?.toLowerCase().replace(/\s+/g, '-');
                  const categoryName = category.name || category.categoryId || 'Unknown';
                  
                  return (
                    <option key={categoryId} value={categoryId}>
                      {categoryName}
                    </option>
                  );
                })}
              </select>
              
              {/* Loading indicator for categories */}
              {categoriesLoading && (
                <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                  <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
                </div>
              )}
            </div>
          </div>
          
          {/* Filter status info */}
          <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
            <div>
              {categories.length > 0 && !categoriesLoading && (
                <span>{categories.length} categories loaded</span>
              )}
            </div>
            <div>
              {(searchTerm || selectedFilter !== 'all') && (
                <span>
                  Showing {filteredAssignments.length} of {assignments.length} items
                  {searchTerm && ` • Search: "${searchTerm}"`}
                  {selectedFilter !== 'all' && ` • Category: ${selectedFilter}`}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Equipment List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-gray-600">Loading equipment...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="text-red-600 mb-2">⚠️</div>
                <p className="text-red-600 mb-4">{error}</p>
                <button
                  onClick={loadOfficerEquipment}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : filteredAssignments.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">
                  {searchTerm || selectedFilter !== 'all'
                    ? 'No equipment matches your search criteria'
                    : 'No equipment currently assigned'
                  }
                </p>
                {(searchTerm || selectedFilter !== 'all') && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedFilter('all');
                    }}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Category header with icon when filtering */}
              {selectedFilter !== 'all' && (
                <div className="flex items-center space-x-2 mb-4 pb-2 border-b border-gray-200">
                  {(() => {
                    const selectedCategory = categories.find(cat => 
                      cat.categoryId === selectedFilter || 
                      cat.name?.toLowerCase().replace(/\s+/g, '-') === selectedFilter ||
                      cat.name?.toLowerCase().replace(/\s+/g, '') === selectedFilter
                    );
                    const CategoryIcon = getCategoryIcon(selectedCategory);
                    return (
                      <>
                        <CategoryIcon className="w-5 h-5 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">
                          {selectedCategory?.name || selectedFilter} Equipment
                        </span>
                        <span className="text-sm text-gray-500">
                          ({filteredAssignments.length} {filteredAssignments.length === 1 ? 'item' : 'items'})
                        </span>
                      </>
                    );
                  })()}
                </div>
              )}
              
              {filteredAssignments.map((assignment) => (
                <EquipmentItemCard
                  key={assignment.assignmentId}
                  assignment={assignment}
                  onReturn={handleReturnItem}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Return Modal */}
      {returnModalOpen && selectedAssignment && (
        <ReturnItemFromOfficerModal
          isOpen={returnModalOpen}
          assignment={selectedAssignment}
          onClose={() => {
            setReturnModalOpen(false);
            setSelectedAssignment(null);
          }}
          onReturnComplete={handleReturnComplete}
        />
      )}
    </div>
  );
};

export default OfficerEquipmentModal;